// app/ningenie/lib/ningenie-repository.ts
// baby: Scalable Firestore repository using subcollections
// Migration from single document to: users/{uid}/accounts, transactions, categories, budgets

import {
  getFirestore,
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  writeBatch,
  runTransaction,
  onSnapshot
} from 'firebase/firestore'
import { app } from '@/app/lib/firebase'
import {
  NINGENIE_MOCK_USER,
  NINGENIE_SEED
} from './ningenie-seed'
import { getNinGenieUserId } from './ningenie-user'
import { DEFAULT_CATEGORIES, DEFAULT_ACCOUNTS } from './ningenie-defaults'
import {
  NinGenieAccount,
  NinGenieAccountInput,
  NinGenieBudget,
  NinGenieBudgetInput,
  NinGenieCategory,
  NinGenieCategoryInput,
  NinGenieSnapshot,
  NinGenieTransaction,
  NinGenieTransactionInput,
  NinGenieSummary,
  ninGenieAccountInputSchema,
  ninGenieBudgetInputSchema,
  ninGenieCategoryInputSchema,
  ninGenieSnapshotSchema,
  ninGenieTransactionInputSchema
} from './ningenie-types'

const db = getFirestore(app)

const sanitizeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_')

// baby: get user ID for collection paths - uses Firebase UID when authenticated
const getUserId = () => getNinGenieUserId()

// baby: collection path helpers
// baby: user document path: users/{userId}
const userDoc = () => doc(db, 'users', getUserId())
const accountsCollection = () => collection(userDoc(), 'accounts')
const transactionsCollection = () => collection(userDoc(), 'transactions')
const categoriesCollection = () => collection(userDoc(), 'categories')
const budgetsCollection = () => collection(userDoc(), 'budgets')

// baby: remove undefined values from objects (Firestore doesn't accept undefined)
const removeUndefined = <T extends Record<string, unknown>>(obj: T): T => {
  const cleaned = {} as T
  for (const key in obj) {
    const value = obj[key]
    if (value !== undefined) {
      if (Array.isArray(value)) {
        cleaned[key] = value.filter(item => item !== undefined) as T[Extract<keyof T, string>]
      } else if (value !== null && typeof value === 'object') {
        cleaned[key] = removeUndefined(value as Record<string, unknown>) as T[Extract<keyof T, string>]
      } else {
        cleaned[key] = value as T[Extract<keyof T, string>]
      }
    }
  }
  return cleaned
}

const generateId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

// baby: recalculate budget spent from transactions
const recalculateBudgetSpent = async (
  budgets: NinGenieBudget[],
  transactions: NinGenieTransaction[]
): Promise<NinGenieBudget[]> => {
  return budgets.map(budget => {
    if (budget.period !== 'monthly') return budget

    const budgetParts = budget.periodLabel.split(' ')
    if (budgetParts.length !== 2) return budget

    const budgetYear = parseInt('20' + budgetParts[1])
    const budgetMonthName = budgetParts[0].toLowerCase()
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    const budgetMonth = monthNames.findIndex(m => m === budgetMonthName.slice(0, 3))

    if (budgetMonth === -1) return budget

    const budgetSpent = transactions
      .filter(txn => {
        if (txn.type !== 'expense' || txn.categoryId !== budget.categoryId) return false
        
        const txnDate = new Date(txn.timestamp)
        const txnYear = txnDate.getFullYear()
        const txnMonth = txnDate.getMonth()
        
        return budgetYear === txnYear && budgetMonth === txnMonth
      })
      .reduce((acc, txn) => acc + txn.amount, 0)

    return {
      ...budget,
      spent: budgetSpent
    }
  })
}

// baby: fetch all data from subcollections and build snapshot
// baby: automatically migrates from old structure if needed
export const fetchNinGenieSnapshot = async (): Promise<NinGenieSnapshot> => {
  try {
    // baby: wait for auth to be ready before checking
    const { isUsingMockData: isUsingMockDataAsync } = await import('./ningenie-user')
    const isMock = await isUsingMockDataAsync()
    const userId = getUserId()
    console.log('[NinGenie] Fetching snapshot for userId:', userId, 'isMock:', isMock)
    
    // baby: check if user document exists (check for accounts subcollection)
    const initialAccountsSnapshot = await getDocs(accountsCollection())
    console.log('[NinGenie] Accounts collection size:', initialAccountsSnapshot.size)
    
    if (initialAccountsSnapshot.empty) {
      console.log('[NinGenie] Accounts collection is empty, initializing data...')
      
      // baby: check if old structure exists and migrate
      const { isMigrationNeeded, migrateToSubcollections } = await import('./migrate-to-subcollections')
      if (await isMigrationNeeded()) {
        console.log('[NinGenie] Old structure detected, migrating to subcollections...')
        await migrateToSubcollections()
        // baby: fetch again after migration
        return fetchNinGenieSnapshot()
      }
      
      // baby: for new authenticated users, seed only categories and accounts (no transactions/budgets)
      // baby: for mock user, seed full mock data
      if (isMock) {
        console.log('[NinGenie] Initializing mock user with full seed data')
        const result = await initializeSeedData()
        console.log('[NinGenie] Seed data initialized, accounts:', result.accounts.length, 'categories:', result.categories.length)
        return result
      } else {
        console.log('[NinGenie] Initializing new authenticated user with default categories and accounts (zero balances)')
        const result = await initializeDefaultData()
        console.log('[NinGenie] Default data initialized, accounts:', result.accounts.length, 'categories:', result.categories.length)
        return result
      }
    }

    // baby: fetch all subcollections in parallel
    const [accountsSnapshot, transactionsSnapshot, categoriesSnapshot, budgetsSnapshot, userDocSnap] = await Promise.all([
      getDocs(accountsCollection()),
      getDocs(query(transactionsCollection(), orderBy('timestamp', 'desc'))),
      getDocs(categoriesCollection()),
      getDocs(budgetsCollection()),
      getDoc(userDoc())
    ])

    // baby: parse data from snapshots
    const accounts: NinGenieAccount[] = accountsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NinGenieAccount))

    const transactions: NinGenieTransaction[] = transactionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NinGenieTransaction))

    const categories: NinGenieCategory[] = categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NinGenieCategory))

    const budgets: NinGenieBudget[] = budgetsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NinGenieBudget))

    // baby: for authenticated users with no transactions, ensure account balances match initialBalance (should be 0)
    // baby: this fixes accounts that were created before the zero-balance fix
    const { isUsingMockDataSync } = await import('./ningenie-user')
    if (!isUsingMockDataSync() && transactions.length === 0) {
      const accountsNeedReset = accounts.filter(acc => acc.balance !== (acc.initialBalance ?? 0))
      
      if (accountsNeedReset.length > 0) {
        console.log('[NinGenie] Resetting account balances to initialBalance (zero) for new user without transactions')
        const batch = writeBatch(db)
        accountsNeedReset.forEach(account => {
          const accountRef = doc(accountsCollection(), account.id)
          batch.update(accountRef, {
            balance: account.initialBalance ?? 0,
            lastUpdated: new Date().toISOString()
          })
        })
        await batch.commit()
        
        // baby: update accounts array with reset balances for immediate UI update
        accounts.forEach(account => {
          if (accountsNeedReset.some(acc => acc.id === account.id)) {
            account.balance = account.initialBalance ?? 0
          }
        })
      }
    }

    // baby: get summary from user document, or calculate from transactions
    const userData = userDocSnap.data()
    let summary: NinGenieSummary = userData?.summary as NinGenieSummary || {
      totalIncome: 0,
      totalExpense: 0,
      totalBalance: 0,
      periodLabel: 'All Time'
    }
    
    // baby: if summary doesn't exist in user doc, calculate it from transactions
    if (!userData?.summary) {
      const allTimeIncome = transactions
        .filter(txn => txn.type === 'income')
        .reduce((acc, txn) => acc + txn.amount, 0)
      
      const allTimeExpense = transactions
        .filter(txn => txn.type === 'expense')
        .reduce((acc, txn) => acc + txn.amount, 0)
      
      summary = {
        totalIncome: allTimeIncome,
        totalExpense: allTimeExpense,
        totalBalance: allTimeIncome - allTimeExpense,
        periodLabel: 'All Time'
      }
      
      // baby: save summary to user document
      await setDoc(userDoc(), { summary }, { merge: true })
    }

    // baby: recalculate budget spent amounts from transactions
    const recalculatedBudgets = await recalculateBudgetSpent(budgets, transactions)

    return {
      accounts,
      transactions,
      categories,
      budgets: recalculatedBudgets,
      summary,
      updatedAt: new Date().toISOString()
    }
  } catch (error) {
    console.error('[NinGenie] Failed to load snapshot from subcollections', error)
    // baby: fallback to seed data
    return await initializeSeedData()
  }
}

// baby: subscribe to NinGenie snapshot with real-time updates
export const subscribeToNinGenieSnapshot = (
  callback: (snapshot: NinGenieSnapshot) => void,
  onError?: (error: Error) => void
): (() => void) => {
  console.log('[NinGenie] Setting up real-time subscription')
  
  // baby: track latest data from each collection
  let latestAccounts: NinGenieAccount[] = []
  let latestTransactions: NinGenieTransaction[] = []
  let latestCategories: NinGenieCategory[] = []
  let latestBudgets: NinGenieBudget[] = []
  let latestSummary: NinGenieSummary = {
    totalIncome: 0,
    totalExpense: 0,
    totalBalance: 0,
    periodLabel: 'All Time'
  }
  
  // baby: flag to track if initial data is loaded
  let collectionsLoaded = {
    accounts: false,
    transactions: false,
    categories: false,
    budgets: false,
    user: false
  }
  
  // baby: helper to check if all collections are loaded and trigger callback
  const tryTriggerCallback = () => {
    const allLoaded = Object.values(collectionsLoaded).every(v => v)
    if (allLoaded) {
      // baby: recalculate budget spent amounts using the same logic as fetchNinGenieSnapshot
      const recalculatedBudgets = latestBudgets.map(budget => {
        if (budget.period !== 'monthly') return budget

        const budgetParts = budget.periodLabel.split(' ')
        if (budgetParts.length !== 2) return budget

        const budgetYear = parseInt('20' + budgetParts[1])
        const budgetMonthName = budgetParts[0].toLowerCase()
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        const budgetMonth = monthNames.findIndex(m => m === budgetMonthName.slice(0, 3))

        if (budgetMonth === -1) return budget

        const budgetSpent = latestTransactions
          .filter(txn => {
            if (txn.type !== 'expense' || txn.categoryId !== budget.categoryId) return false
            
            const txnDate = new Date(txn.timestamp)
            const txnYear = txnDate.getFullYear()
            const txnMonth = txnDate.getMonth()
            
            return budgetYear === txnYear && budgetMonth === txnMonth
          })
          .reduce((acc, txn) => acc + txn.amount, 0)

        return {
          ...budget,
          spent: budgetSpent
        }
      })
      
      callback({
        accounts: latestAccounts,
        transactions: latestTransactions,
        categories: latestCategories,
        budgets: recalculatedBudgets,
        summary: latestSummary,
        updatedAt: new Date().toISOString()
      })
    }
  }
  
  // baby: subscribe to accounts collection
  const unsubAccounts = onSnapshot(
    accountsCollection(),
    (snapshot) => {
      latestAccounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NinGenieAccount))
      collectionsLoaded.accounts = true
      tryTriggerCallback()
    },
    (error) => {
      console.error('[NinGenie] Error in accounts subscription:', error)
      onError?.(error as Error)
    }
  )
  
  // baby: subscribe to transactions collection
  const unsubTransactions = onSnapshot(
    query(transactionsCollection(), orderBy('timestamp', 'desc')),
    (snapshot) => {
      latestTransactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NinGenieTransaction))
      
      // baby: recalculate summary from transactions (exclude transfers like updateSummary does)
      const allTimeIncome = latestTransactions
        .filter(txn => txn.type === 'income' && !txn.counterpartyAccountId) // exclude transfers
        .reduce((acc, txn) => acc + txn.amount, 0)
      
      const allTimeExpense = latestTransactions
        .filter(txn => txn.type === 'expense' && !txn.counterpartyAccountId) // exclude transfers
        .reduce((acc, txn) => acc + txn.amount, 0)
      
      latestSummary = {
        totalIncome: allTimeIncome,
        totalExpense: allTimeExpense,
        totalBalance: allTimeIncome - allTimeExpense,
        periodLabel: 'All Time'
      }
      
      collectionsLoaded.transactions = true
      tryTriggerCallback()
    },
    (error) => {
      console.error('[NinGenie] Error in transactions subscription:', error)
      onError?.(error as Error)
    }
  )
  
  // baby: subscribe to categories collection
  const unsubCategories = onSnapshot(
    categoriesCollection(),
    (snapshot) => {
      latestCategories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NinGenieCategory))
      collectionsLoaded.categories = true
      tryTriggerCallback()
    },
    (error) => {
      console.error('[NinGenie] Error in categories subscription:', error)
      onError?.(error as Error)
    }
  )
  
  // baby: subscribe to budgets collection
  const unsubBudgets = onSnapshot(
    budgetsCollection(),
    (snapshot) => {
      latestBudgets = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NinGenieBudget))
      collectionsLoaded.budgets = true
      tryTriggerCallback()
    },
    (error) => {
      console.error('[NinGenie] Error in budgets subscription:', error)
      onError?.(error as Error)
    }
  )
  
  // baby: subscribe to user document for summary
  const unsubUser = onSnapshot(
    userDoc(),
    (snapshot) => {
      const userData = snapshot.data()
      if (userData?.summary) {
        latestSummary = userData.summary as NinGenieSummary
      }
      collectionsLoaded.user = true
      tryTriggerCallback()
    },
    (error) => {
      console.error('[NinGenie] Error in user document subscription:', error)
      onError?.(error as Error)
    }
  )
  
  // baby: return unsubscribe function that cleans up all subscriptions
  return () => {
    console.log('[NinGenie] Cleaning up real-time subscriptions')
    unsubAccounts()
    unsubTransactions()
    unsubCategories()
    unsubBudgets()
    unsubUser()
  }
}

// baby: initialize seed data in subcollections (for mock user - includes transactions/budgets)
const initializeSeedData = async (): Promise<NinGenieSnapshot> => {
  const seed = NINGENIE_SEED
  const batch = writeBatch(db)
  const nowIso = new Date().toISOString()

  // baby: write accounts to subcollection
  seed.accounts.forEach(account => {
    const accountRef = doc(accountsCollection(), account.id)
    batch.set(accountRef, removeUndefined(account as Record<string, unknown>))
  })

  // baby: write transactions to subcollection
  seed.transactions.forEach(transaction => {
    const txnRef = doc(transactionsCollection(), transaction.id)
    batch.set(txnRef, removeUndefined(transaction as Record<string, unknown>))
  })

  // baby: write categories to subcollection
  seed.categories.forEach(category => {
    const categoryRef = doc(categoriesCollection(), category.id)
    batch.set(categoryRef, removeUndefined(category as Record<string, unknown>))
  })

  // baby: write budgets to subcollection
  seed.budgets.forEach(budget => {
    const budgetRef = doc(budgetsCollection(), budget.id)
    batch.set(budgetRef, removeUndefined(budget as Record<string, unknown>))
  })

  // baby: write summary to user document
  batch.set(userDoc(), {
    summary: seed.summary,
    updatedAt: nowIso
  }, { merge: true })

  await batch.commit()

  return {
    ...seed,
    updatedAt: nowIso
  }
}

// baby: initialize default data for new authenticated users (categories and accounts only, no transactions/budgets)
const initializeDefaultData = async (): Promise<NinGenieSnapshot> => {
  console.log('[NinGenie] Starting initializeDefaultData...')
  const batch = writeBatch(db)
  const nowIso = new Date().toISOString()
  const userId = getUserId()
  
  console.log('[NinGenie] User ID for seeding:', userId)
  console.log('[NinGenie] Accounts to create:', DEFAULT_ACCOUNTS.length)
  console.log('[NinGenie] Categories to create:', DEFAULT_CATEGORIES.length)

  // baby: write default categories to subcollection
  DEFAULT_CATEGORIES.forEach(category => {
    const categoryRef = doc(categoriesCollection(), category.id)
    const categoryData = removeUndefined(category as Record<string, unknown>)
    batch.set(categoryRef, categoryData)
    console.log('[NinGenie] Adding category:', category.id, category.name)
  })

  // baby: write default accounts to subcollection (with zero balances)
  // baby: explicitly set balance and initialBalance to 0 to ensure no random values
  DEFAULT_ACCOUNTS.forEach(account => {
    const accountRef = doc(accountsCollection(), account.id)
    const accountData = {
      ...account,
      initialBalance: 0,
      balance: 0
    }
    const cleanedData = removeUndefined(accountData as Record<string, unknown>)
    batch.set(accountRef, cleanedData)
    console.log('[NinGenie] Adding account:', account.id, account.name, 'balance:', cleanedData.balance, 'initialBalance:', cleanedData.initialBalance)
  })

  // baby: initialize empty summary for new user
  const emptySummary: NinGenieSummary = {
    totalIncome: 0,
    totalExpense: 0,
    totalBalance: 0,
    periodLabel: 'All Time'
  }

  // baby: write summary to user document
  const userDocRef = userDoc()
  console.log('[NinGenie] User document path:', userDocRef.path)
  batch.set(userDocRef, {
    summary: emptySummary,
    updatedAt: nowIso
  }, { merge: true })

  console.log('[NinGenie] Committing batch write...')
  await batch.commit()
  console.log('[NinGenie] Batch write committed successfully')

  const result = {
    accounts: DEFAULT_ACCOUNTS,
    categories: DEFAULT_CATEGORIES,
    transactions: [],
    budgets: [],
    summary: emptySummary,
    updatedAt: nowIso
  }
  
  console.log('[NinGenie] initializeDefaultData complete, returning:', {
    accountsCount: result.accounts.length,
    categoriesCount: result.categories.length,
    allBalancesZero: result.accounts.every(acc => acc.balance === 0)
  })
  
  return result
}

// baby: update summary in user document
const updateSummary = async (transactions: NinGenieTransaction[]): Promise<void> => {
  // baby: exclude transfers (transactions with counterpartyAccountId) from income/expense calculations
  const allTimeIncome = transactions
    .filter(txn => txn.type === 'income' && !txn.counterpartyAccountId) // exclude transfers
    .reduce((acc, txn) => acc + txn.amount, 0)

  const allTimeExpense = transactions
    .filter(txn => txn.type === 'expense' && !txn.counterpartyAccountId) // exclude transfers
    .reduce((acc, txn) => acc + txn.amount, 0)

  const summary: NinGenieSummary = {
    totalIncome: allTimeIncome,
    totalExpense: allTimeExpense,
    totalBalance: allTimeIncome - allTimeExpense,
    periodLabel: 'All Time'
  }

  await setDoc(userDoc(), { summary }, { merge: true })
}

// baby: upsert account to subcollection
export const upsertNinGenieAccount = async (
  input: NinGenieAccountInput
): Promise<NinGenieSnapshot> => {
  const parsed = ninGenieAccountInputSchema.parse(input)
  const nowIso = new Date().toISOString()

  const accountId = parsed.id ?? generateId('account')
  const accountRef = doc(accountsCollection(), accountId)

  // baby: if balance is not provided, calculate from initialBalance (for new accounts)
  // baby: if balance is provided, use it (for updates)
  const accountBalance = parsed.balance !== undefined 
    ? parsed.balance 
    : (parsed.initialBalance ?? 0)

  const account: NinGenieAccount = {
    id: accountId,
    name: parsed.name,
    type: parsed.type,
    initialBalance: parsed.initialBalance ?? 0,
    balance: accountBalance,
    currency: parsed.currency || 'INR',
    iconKey: parsed.iconKey || 'default',
    color: parsed.color || '#21728d',
    lastUpdated: nowIso,
    ...(parsed.institution && { institution: parsed.institution })
  }

  await setDoc(accountRef, removeUndefined(account as Record<string, unknown>))
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: delete account from subcollection
// baby: warning: this does not check for existing transactions - user should handle cleanup
export const deleteNinGenieAccount = async (
  accountId: string
): Promise<NinGenieSnapshot> => {
  const accountRef = doc(accountsCollection(), accountId)
  await deleteDoc(accountRef)
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: upsert category to subcollection
export const upsertNinGenieCategory = async (
  input: NinGenieCategoryInput
): Promise<NinGenieSnapshot> => {
  const parsed = ninGenieCategoryInputSchema.parse(input)

  const categoryId = parsed.id ?? generateId('category')
  const categoryRef = doc(categoriesCollection(), categoryId)

  const category: NinGenieCategory = {
    id: categoryId,
    name: parsed.name,
    type: parsed.type,
    iconKey: parsed.iconKey || 'default',
    color: parsed.color || '#88a1ac',
    ...(parsed.notes && { notes: parsed.notes })
  }

  await setDoc(categoryRef, removeUndefined(category as Record<string, unknown>))
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: delete category from subcollection
// baby: warning: this does not check for existing transactions/budgets - user should handle cleanup
export const deleteNinGenieCategory = async (
  categoryId: string
): Promise<NinGenieSnapshot> => {
  const categoryRef = doc(categoriesCollection(), categoryId)
  await deleteDoc(categoryRef)
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: upsert budget to subcollection
export const upsertNinGenieBudget = async (
  input: NinGenieBudgetInput
): Promise<NinGenieSnapshot> => {
  const parsed = ninGenieBudgetInputSchema.parse(input)
  const nowIso = new Date().toISOString()

  // baby: fetch current transactions to calculate spent
  const transactionsSnapshot = await getDocs(query(transactionsCollection(), orderBy('timestamp', 'desc')))
  const transactions: NinGenieTransaction[] = transactionsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as NinGenieTransaction))

  // baby: calculate spent from existing transactions if not explicitly provided
  let calculatedSpent = parsed.spent
  
  if (calculatedSpent === undefined) {
    const budgetParts = parsed.periodLabel.split(' ')
    if (budgetParts.length !== 2) {
      calculatedSpent = 0
    } else {
      const budgetYear = parseInt('20' + budgetParts[1])
      const budgetMonthName = budgetParts[0].toLowerCase()
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
      const budgetMonth = monthNames.findIndex(m => m === budgetMonthName.slice(0, 3))
      
      if (budgetMonth === -1) {
        calculatedSpent = 0
      } else {
        calculatedSpent = transactions
          .filter(txn => {
            if (txn.type !== 'expense' || txn.categoryId !== parsed.categoryId) return false
            
            const txnDate = new Date(txn.timestamp)
            const txnYear = txnDate.getFullYear()
            const txnMonth = txnDate.getMonth()
            
            return budgetYear === txnYear && budgetMonth === txnMonth
          })
          .reduce((acc, txn) => acc + txn.amount, 0)
      }
    }
  }

  const budgetId = parsed.id ?? generateId('budget')
  const budgetRef = doc(budgetsCollection(), budgetId)

  // baby: fetch existing budget if updating
  let existing: NinGenieBudget | undefined
  if (parsed.id) {
    const existingSnapshot = await getDoc(budgetRef)
    if (existingSnapshot.exists()) {
      existing = { id: existingSnapshot.id, ...existingSnapshot.data() } as NinGenieBudget
    }
  }

  const budget: NinGenieBudget = {
    ...existing,
    ...parsed,
    id: budgetId,
    spent: calculatedSpent,
    createdAt: existing?.createdAt ?? nowIso,
    updatedAt: nowIso
  }

  await setDoc(budgetRef, removeUndefined(budget as Record<string, unknown>))
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: delete budget from subcollection
export const deleteNinGenieBudget = async (
  budgetId: string
): Promise<NinGenieSnapshot> => {
  const budgetRef = doc(budgetsCollection(), budgetId)
  await deleteDoc(budgetRef)
  
  // baby: return updated snapshot
  return fetchNinGenieSnapshot()
}

// baby: add transaction using Firestore transaction for atomic updates
export const addNinGenieTransaction = async (
  input: NinGenieTransactionInput
): Promise<NinGenieSnapshot> => {
  const parsed = ninGenieTransactionInputSchema.parse(input)
  const nowIso = new Date().toISOString()

  return await runTransaction(db, async (transaction) => {
    let transactionsToCreate: NinGenieTransaction[] = []
    const accountUpdates: { accountId: string; delta: number }[] = []

    // baby: FIRST: read all accounts and categories needed for names (Firestore requires all reads before writes)
    const accountIdsToRead = new Set<string>()
    accountIdsToRead.add(parsed.accountId)
    if (parsed.counterpartyAccountId) {
      accountIdsToRead.add(parsed.counterpartyAccountId)
    }
    
    const accountDataMap = new Map<string, NinGenieAccount>()
    const accountSnapshotMap = new Map<string, Awaited<ReturnType<typeof transaction.get>>>()
    const categoryDataMap = new Map<string, NinGenieCategory>()
    
    // baby: read all accounts (store both data and snapshot refs for later updates)
    for (const accountId of accountIdsToRead) {
      const accountRef = doc(accountsCollection(), accountId)
      const accountSnap = await transaction.get(accountRef)
      
      if (!accountSnap.exists()) {
        throw new Error(`Account ${accountId} not found`)
      }
      
      accountDataMap.set(accountId, accountSnap.data() as NinGenieAccount)
      accountSnapshotMap.set(accountId, accountSnap)
    }
    
    // baby: read category if provided
    if (parsed.categoryId) {
      const categoryRef = doc(categoriesCollection(), parsed.categoryId)
      const categorySnap = await transaction.get(categoryRef)
      
      if (categorySnap.exists()) {
        categoryDataMap.set(parsed.categoryId, categorySnap.data() as NinGenieCategory)
      }
    }
    
    // baby: get account names
    const accountName = accountDataMap.get(parsed.accountId)?.name || ''
    const counterpartyAccountName = parsed.counterpartyAccountId 
      ? accountDataMap.get(parsed.counterpartyAccountId)?.name || null 
      : null
    const categoryName = parsed.categoryId && categoryDataMap.has(parsed.categoryId)
      ? categoryDataMap.get(parsed.categoryId)?.name || null
      : null

    if (parsed.type === 'transfer') {
      // baby: transfers create TWO transactions - one expense from source, one income to destination
      if (!parsed.counterpartyAccountId) {
        throw new Error('Transfer requires a counterparty account')
      }

      const sourceTxn: NinGenieTransaction = {
        id: generateId('txn'),
        type: 'expense',
        accountId: parsed.accountId,
        accountName: accountName,
        counterpartyAccountId: parsed.counterpartyAccountId,
        counterpartyAccountName: counterpartyAccountName,
        categoryId: null,
        categoryName: null,
        amount: parsed.amount,
        currency: parsed.currency || 'INR',
        timestamp: parsed.timestamp,
        tags: parsed.tags || [],
        createdAt: nowIso,
        note: parsed.note || 'Transfer'
      }

      const destTxn: NinGenieTransaction = {
        id: generateId('txn'),
        type: 'income',
        accountId: parsed.counterpartyAccountId,
        accountName: counterpartyAccountName || '',
        counterpartyAccountId: parsed.accountId,
        counterpartyAccountName: accountName,
        categoryId: null,
        categoryName: null,
        amount: parsed.amount,
        currency: parsed.currency || 'INR',
        timestamp: parsed.timestamp,
        tags: parsed.tags || [],
        createdAt: nowIso,
        note: parsed.note || 'Transfer'
      }

      transactionsToCreate = [sourceTxn, destTxn]
      accountUpdates.push(
        { accountId: parsed.accountId, delta: -parsed.amount },
        { accountId: parsed.counterpartyAccountId, delta: parsed.amount }
      )
    } else {
      // baby: income/expense transactions create single transaction
      const txn: NinGenieTransaction = {
        id: parsed.id ?? generateId('txn'),
        type: parsed.type,
        accountId: parsed.accountId,
        accountName: accountName,
        amount: parsed.amount,
        currency: parsed.currency || 'INR',
        timestamp: parsed.timestamp,
        tags: parsed.tags || [],
        createdAt: parsed.createdAt ?? nowIso,
        ...(parsed.counterpartyAccountId !== undefined && { 
          counterpartyAccountId: parsed.counterpartyAccountId,
          counterpartyAccountName: counterpartyAccountName
        }),
        ...(parsed.categoryId !== undefined && { 
          categoryId: parsed.categoryId,
          categoryName: categoryName
        }),
        ...(parsed.note && { note: parsed.note })
      }

      transactionsToCreate = [txn]
      
      // baby: update account balance
      const delta = parsed.type === 'income' ? parsed.amount : -parsed.amount
      accountUpdates.push({ accountId: parsed.accountId, delta })
    }

    // baby: reuse account snapshots for balance updates (already read above for names)
    const accountSnapshots: Array<{ snap: Awaited<ReturnType<typeof transaction.get>>; delta: number }> = []
    
    for (const { accountId, delta } of accountUpdates) {
      // baby: reuse snapshot if we already read it, otherwise read it now
      const accountSnap = accountSnapshotMap.get(accountId) || await transaction.get(doc(accountsCollection(), accountId))
      
      if (!accountSnap.exists()) {
        throw new Error(`Account ${accountId} not found`)
      }
      
      accountSnapshots.push({ snap: accountSnap, delta })
    }
    
    // baby: THEN: perform all writes (accounts and transactions)
    for (const { snap, delta } of accountSnapshots) {
      const accountData = snap.data() as NinGenieAccount
      // baby: ensure balance exists, default to initialBalance or 0
      const currentBalance = accountData.balance ?? accountData.initialBalance ?? 0
      const newBalance = currentBalance + delta
      console.log('[NinGenie] Updating account balance:', {
        accountId: accountData.id,
        accountName: accountData.name,
        currentBalance,
        delta,
        newBalance,
        transactionType: parsed.type,
        amount: parsed.amount
      })
      transaction.update(snap.ref, {
        balance: newBalance,
        lastUpdated: nowIso
      })
    }
    
    // baby: write transactions to subcollection
    for (const txn of transactionsToCreate) {
      const txnRef = doc(transactionsCollection(), txn.id)
      transaction.set(txnRef, removeUndefined(txn as Record<string, unknown>))
    }

    // baby: note: summary update will happen after transaction via fetchNinGenieSnapshot
    // In production, use Cloud Function to update summary atomically
  }).then(async () => {
    // baby: update summary after transaction completes
    console.log('[NinGenie] Transaction created, updating summary...')
    const snapshot = await fetchNinGenieSnapshot()
    console.log('[NinGenie] Snapshot fetched, accounts:', snapshot.accounts.map(a => ({ id: a.id, name: a.name, balance: a.balance })))
    await updateSummary(snapshot.transactions)
    console.log('[NinGenie] Summary updated')
    const finalSnapshot = await fetchNinGenieSnapshot()
    console.log('[NinGenie] Final snapshot fetched, accounts:', finalSnapshot.accounts.map(a => ({ id: a.id, name: a.name, balance: a.balance })))
    return finalSnapshot
  })
}

// baby: delete transaction using Firestore transaction for atomic updates
export const deleteNinGenieTransaction = async (
  transactionId: string
): Promise<NinGenieSnapshot> => {
  // baby: fetch transaction outside transaction to find paired transfer
  const txnSnap = await getDoc(doc(transactionsCollection(), transactionId))
  if (!txnSnap.exists()) {
    throw new Error('Transaction not found')
  }

  const transactionToDelete = { id: txnSnap.id, ...txnSnap.data() } as NinGenieTransaction

  // baby: check if this is part of a transfer (has counterpartyAccountId)
  const isTransfer = !!transactionToDelete.counterpartyAccountId
  let transactionsToRemove: NinGenieTransaction[] = [transactionToDelete]
  
  if (isTransfer) {
    // baby: find paired transaction outside transaction
    const allTransactionsSnapshot = await getDocs(query(transactionsCollection(), orderBy('timestamp', 'desc')))
    const pairedTransaction = allTransactionsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as NinGenieTransaction))
      .find(
        txn => txn.id !== transactionId &&
        txn.counterpartyAccountId === transactionToDelete.accountId &&
        txn.accountId === transactionToDelete.counterpartyAccountId &&
        txn.timestamp === transactionToDelete.timestamp &&
        Math.abs(txn.amount - transactionToDelete.amount) < 0.01
      )
    
    if (pairedTransaction) {
      transactionsToRemove.push(pairedTransaction)
    }
  }

  return await runTransaction(db, async (transaction) => {
    // baby: reverse account balance changes
    const nowIso = new Date().toISOString()
    
    // baby: FIRST: read all accounts (Firestore requires all reads before writes)
    const accountUpdates: Array<{ ref: ReturnType<typeof doc>; delta: number; snap: Awaited<ReturnType<typeof transaction.get>> }> = []
    
    for (const txn of transactionsToRemove) {
      const accountRef = doc(accountsCollection(), txn.accountId)
      const accountSnap = await transaction.get(accountRef)
      
      if (accountSnap.exists()) {
        const currentBalance = accountSnap.data().balance as number
        const delta = txn.type === 'income' ? -txn.amount : txn.amount
        accountUpdates.push({ ref: accountRef, delta, snap: accountSnap })
      }
    }
    
    // baby: THEN: perform all writes (account updates and transaction deletions)
    for (const { ref, delta, snap } of accountUpdates) {
      const accountData = snap.data() as NinGenieAccount
      transaction.update(ref, {
        balance: accountData.balance + delta,
        lastUpdated: nowIso
      })
    }

    // baby: delete transaction documents
    for (const txn of transactionsToRemove) {
      transaction.delete(doc(transactionsCollection(), txn.id))
    }

    // baby: note: summary update will happen after transaction via fetchNinGenieSnapshot
    // In production, use Cloud Function to update summary atomically
  }).then(async () => {
    // baby: update summary after transaction completes
    const snapshot = await fetchNinGenieSnapshot()
    await updateSummary(snapshot.transactions)
    return fetchNinGenieSnapshot()
  })
}
